import { useState } from 'react';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';

import {
  DateRangeValue,
  RangeConstraints,
  VALUE_FORMAT,
  exceedsMaxDays,
  formatRangeLabel,
  parseDay,
} from './utils';

dayjs.extend(utc);

const I18N_BASE = 'components.date_range_preset_picker';
const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const GRID_DAYS = 42;

interface DateRangeCalendarProps extends RangeConstraints {
  /**
   * The range in force: it starts selected, its last month is shown first and
   * applying the very same range is not offered.
   */
  value: DateRangeValue;
  /** The current day, `YYYY-MM-DD`, marked in the grid. */
  today: string;
  onApply: (range: DateRangeValue) => void;
}

/** The 6x7 grid of a month, starting on Monday. */
const buildGrid = (month: Dayjs): string[] => {
  const first = month.startOf('month');
  const start = first.subtract((first.day() + 6) % 7, 'day');

  return Array.from({ length: GRID_DAYS }, (_, index) =>
    start.add(index, 'day').format(VALUE_FORMAT)
  );
};

/**
 * Single-month calendar to pick a range: it opens with `value` selected, the
 * next click is a new start, the one after the end (in any order) and a third
 * starts over. The caller applies the range from the footer button once both
 * ends are set and differ from `value`.
 */
function DateRangeCalendar({
  value,
  today,
  disabledDate,
  maxDays,
  onApply,
}: DateRangeCalendarProps) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => parseDay(value.to).startOf('month'));
  const [start, setStart] = useState<string | null>(value.from);
  const [end, setEnd] = useState<string | null>(value.to);
  const [hover, setHover] = useState<string | null>(null);

  const awaitingEnd = start !== null && end === null;
  // While the end is pending, the hovered day previews the range.
  const previewEnd = awaitingEnd ? hover : end;
  // The endpoints are ordered once; preview, Apply and the label share them.
  const [low, high] =
    start && previewEnd && previewEnd < start
      ? [previewEnd, start]
      : [start, previewEnd];
  const complete = start !== null && end !== null;

  const isDisabled = (date: string): boolean => {
    const current = parseDay(date);
    const picked = awaitingEnd ? parseDay(start) : undefined;

    if (picked && maxDays !== undefined) {
      if (exceedsMaxDays(current, picked, maxDays)) return true;
    }

    return Boolean(disabledDate?.(current, picked));
  };

  const pick = (date: string) => {
    if (isDisabled(date)) return;
    if (!awaitingEnd) {
      setStart(date);
      setEnd(null);

      return;
    }
    setEnd(date);
  };

  const changed = complete && (low !== value.from || high !== value.to);
  const apply = () => {
    if (changed && low && high) onApply({ from: low, to: high });
  };

  const monthLabel = `${t(
    `common.months.${MONTH_KEYS[month.month()]}`
  )} ${month.year()}`;

  return (
    <div className="drp-calendar">
      <div className="drp-calendar__header">
        <Button
          type="text"
          size="small"
          icon={<LeftOutlined />}
          aria-label={t(`${I18N_BASE}.calendar.previous_month`)}
          onClick={() => setMonth(month.subtract(1, 'month'))}
        />
        <span className="drp-calendar__title">{monthLabel}</span>
        <Button
          type="text"
          size="small"
          icon={<RightOutlined />}
          aria-label={t(`${I18N_BASE}.calendar.next_month`)}
          onClick={() => setMonth(month.add(1, 'month'))}
        />
      </div>

      <div className="drp-calendar__weekdays">
        {WEEKDAY_KEYS.map((key) => (
          <span key={key} className="drp-calendar__weekday">
            {t(`${I18N_BASE}.calendar.weekdays.${key}`)}
          </span>
        ))}
      </div>

      <div className="drp-calendar__grid" onMouseLeave={() => setHover(null)}>
        {buildGrid(month).map((date) => {
          const inMonth = date.slice(0, 7) === month.format('YYYY-MM');
          const isStart = date === low;
          const isEnd = date === high;
          const inRange = Boolean(low && high && date > low && date < high);
          const classes = [
            'drp-day',
            !inMonth && 'drp-day--outside',
            date === today && 'drp-day--today',
            inRange && 'drp-day--in-range',
            isStart && 'drp-day--start',
            isEnd && 'drp-day--end',
            isStart && isEnd && 'drp-day--single',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={date}
              type="button"
              className={classes}
              data-date={date}
              disabled={isDisabled(date)}
              onClick={() => pick(date)}
              onMouseEnter={() => setHover(date)}
            >
              {Number(date.slice(8))}
            </button>
          );
        })}
      </div>

      <div className="drp-calendar__footer">
        <span className="drp-calendar__hint">
          {complete && low && high
            ? formatRangeLabel({ from: low, to: high })
            : t(`${I18N_BASE}.hint`)}
        </span>
        <Button type="primary" size="small" disabled={!changed} onClick={apply}>
          {t(`${I18N_BASE}.apply`)}
        </Button>
      </div>
    </div>
  );
}

export default DateRangeCalendar;
